import { LinkageColumn, LinkedColumn, LinkedColumnType } from '@/features/canvas/types/linkage';

// Define column names that indicate initiative IDs (hidden from mapping)
export const ID_COLUMN_NAMES = [
  'konexis i.d. do not edit', // Normalized version without parentheses
  'konexis i.d. (do not edit)',
  'konexis i.d.',
  'konexis id',
  '_konexis_id',
  'initiative_id',
  'initiative id',
  'id',
];

// Define common aliases for standard fields
// Note: targetId format must match what LinkingDialog expects
const STANDARD_FIELD_MAPPINGS = [
  {
    type: LinkedColumnType.title,
    targetId: `${LinkedColumnType.title}`, // Just 'title'
    targetTitle: 'Title',
    aliases: [
      'title',
      'project title',
      'initiative name',
      'name',
      'project name',
      'initiative',
      'item name',
    ],
  },
  {
    type: LinkedColumnType.description,
    targetId: `${LinkedColumnType.description}`, // Just 'description'
    targetTitle: 'Description',
    aliases: ['description', 'desc', 'details', 'summary', 'overview', 'notes', 'comments'],
  },
  {
    type: LinkedColumnType.date,
    targetId: '1-date-roadmap', // Standard ID format for Start Date
    targetTitle: 'Start Date',
    aliases: [
      'start date',
      'start',
      'begin date',
      'begin',
      'start_date',
      'startdate',
      'starting date',
      'from date',
      'from',
    ],
  },
  {
    type: LinkedColumnType.date,
    targetId: '2-date-roadmap', // Standard ID format for End Date
    targetTitle: 'End Date',
    aliases: [
      'end date',
      'end',
      'finish date',
      'finish',
      'due date',
      'due',
      'end_date',
      'enddate',
      'ending date',
      'to date',
      'to',
      'completion date',
      'target date',
    ],
  },
  {
    type: LinkedColumnType.progressBar,
    targetId: '3-progressBar-roadmap', // Standard ID format for Progress Bar
    targetTitle: 'Progress Bar',
    aliases: [
      'progress',
      'completion',
      'percent complete',
      '% complete',
      // Remove 'status' as it's too generic and often contains text values
      'progress %',
      'complete %',
      'done %',
      'percentage',
      'completion percentage',
    ],
  },
  // Budget Total patterns (including generic budget patterns)
  {
    type: LinkedColumnType.budgetTotal,
    targetId: LinkedColumnType.budgetTotal,
    targetTitle: 'Budget: Total',
    aliases: [
      // Specific total patterns
      'budget: total',
      'budget total',
      'total budget',
      'budget - total',
      'total amount',
      'total cost',
      'total investment',
      // Generic budget patterns (map to total)
      'budget',
      'cost',
      'amount',
      // Remove 'value' as it's too generic and often used in other contexts
      'price',
      'investment',
      'funding',
      'expense',
      'expenditure',
    ],
  },
  {
    type: LinkedColumnType.budgetCapEx,
    targetId: LinkedColumnType.budgetCapEx,
    targetTitle: 'Budget: CapEx',
    aliases: [
      'budget: capex',
      'budget capex',
      'capex',
      'cap ex',
      'capital expenditure',
      'capital expense',
      'budget - capex',
      'capex budget',
    ],
  },
  {
    type: LinkedColumnType.budgetOpEx,
    targetId: LinkedColumnType.budgetOpEx,
    targetTitle: 'Budget: OpEx',
    aliases: [
      'budget: opex',
      'budget opex',
      'opex',
      'op ex',
      'operating expenditure',
      'operating expense',
      'operational expenditure',
      'operational expense',
      'budget - opex',
      'opex budget',
    ],
  },
  {
    type: LinkedColumnType.budgetSpent,
    targetId: LinkedColumnType.budgetSpent,
    targetTitle: 'Budget: Spent',
    aliases: [
      'budget: spent',
      'budget spent',
      'spent',
      'amount spent',
      'spent amount',
      'budget - spent',
      'spent budget',
      'actual spend',
      'actual',
    ],
  },
  {
    type: LinkedColumnType.dependencies,
    targetId: LinkedColumnType.dependencies,
    targetTitle: 'Dependent On',
    aliases: ['dependent on', 'dependencies', 'depends on'],
  },
  // Children is deliberately not auto-mapped: the backend creates a new initiative for each child
  // title not yet on the roadmap, which duplicates children that are also rows in the CSV
];

/**
 * Normalize string for comparison (lowercase, trim, remove special chars)
 */
const normalize = (str: string): string => {
  return str
    .toLowerCase()
    .trim()
    .replace(/[_-]/g, ' ')
    .replace(/[()]/g, '') // Remove parentheses for matching
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Check if two strings match (exact or fuzzy)
 */
const isMatch = (csvHeader: string, alias: string): boolean => {
  const normalizedHeader = normalize(csvHeader);
  const normalizedAlias = normalize(alias);

  // Exact match
  if (normalizedHeader === normalizedAlias) return true;

  // For very short aliases (< 5 chars), require exact match to avoid false positives
  if (normalizedAlias.length < 5) {
    return normalizedHeader === normalizedAlias;
  }

  // For budget/progress fields, be more conservative - require word boundaries
  const budgetProgressKeywords = ['budget', 'cost', 'amount', 'progress', 'complete'];
  const isBudgetProgressAlias = budgetProgressKeywords.some(keyword =>
    normalizedAlias.includes(keyword)
  );

  if (isBudgetProgressAlias) {
    // For budget/progress fields, require the alias to be a whole word in the header
    const words = normalizedHeader.split(' ');
    const aliasWords = normalizedAlias.split(' ');

    // Check if all alias words appear as whole words in header
    return aliasWords.every(aliasWord => words.some(headerWord => headerWord === aliasWord));
  }

  // For other fields, allow contains match but prefer header containing alias
  // (not alias containing header which can be too broad)
  if (normalizedHeader.includes(normalizedAlias)) {
    return true;
  }

  return false;
};

/**
 * Auto-detect column mappings based on CSV headers
 * @param columns CSV columns with id and name
 * @param existingFields Existing fields from roadmap config (roadmapVisible, dropdownVisible, etc.)
 * @param filters Existing filters from roadmap
 * @returns Array of auto-detected LinkedColumn mappings
 */
export const autoDetectMappings = (
  columns: LinkageColumn[],
  existingFields?: { id: number | string; title: string; type: string }[],
  filters?: { id: number | string; title: string }[]
): LinkedColumn[] => {
  const detectedMappings: LinkedColumn[] = [];
  const usedTargetIds = new Set<string>();
  const mappedColumnIds = new Set<string>();

  // Helper to try mapping a column to a target
  const tryMapColumn = (
    column: LinkageColumn,
    targetId: string,
    targetTitle: string,
    targetType: string,
    aliases: string[]
  ): boolean => {
    if (mappedColumnIds.has(column.id) || usedTargetIds.has(targetId)) {
      return false;
    }

    const hasMatch = aliases.some(alias => isMatch(column.name, alias));
    if (hasMatch) {
      detectedMappings.push({
        importedColumnUuid: column.id,
        linkedTargetId: targetId,
        linkedTargetTitle: targetTitle,
        linkedTargetType: targetType as keyof typeof LinkedColumnType,
      });
      usedTargetIds.add(targetId);
      mappedColumnIds.add(column.id);
      return true;
    }
    return false;
  };

  // First pass: Check standard field mappings
  columns.forEach(column => {
    STANDARD_FIELD_MAPPINGS.some(mapping =>
      tryMapColumn(column, mapping.targetId, mapping.targetTitle, mapping.type, mapping.aliases)
    );
  });

  // Second pass: Check custom fields from roadmap config
  if (existingFields) {
    columns
      .filter(column => !mappedColumnIds.has(column.id))
      .forEach(column => {
        existingFields.some(field => {
          const fieldId = `${field.id}-${field.type}`;
          return tryMapColumn(column, fieldId, field.title, field.type, [field.title]);
        });
      });
  }

  // Third pass: Check filters
  if (filters) {
    columns
      .filter(column => !mappedColumnIds.has(column.id))
      .forEach(column => {
        filters.some(filter => {
          const filterId = filter.id?.toString() || '';
          return tryMapColumn(column, filterId, filter.title, LinkedColumnType.filter, [
            filter.title,
          ]);
        });
      });
  }

  return detectedMappings;
};

/**
 * Check if a mapping for Title field exists
 */
export const hasTitleMapping = (mappings: LinkedColumn[]): boolean => {
  return mappings.some(m => m.linkedTargetType === LinkedColumnType.title);
};

/**
 * Detect if a column is an ID column based on its name
 */
export const isIdColumn = (columnName: string): boolean => {
  const normalized = normalize(columnName);
  return ID_COLUMN_NAMES.some(idName => normalized === normalize(idName));
};

/**
 * Find ID column in columns array
 * Checks each ID pattern in priority order (most specific first) to ensure
 * "Konexis I.D. (Do not edit)" is matched before generic "ID"
 * @returns The ID column or null if not found
 */
export const detectIdColumn = (columns: LinkageColumn[]): LinkageColumn | null => {
  // Check each ID pattern in priority order (most specific first)
  const match = ID_COLUMN_NAMES.reduce<LinkageColumn | null>((found, idName) => {
    if (found) return found;
    const normalizedIdName = normalize(idName);
    return columns.find(col => normalize(col.name) === normalizedIdName) || null;
  }, null);

  return match;
};
